import { APP_BUILD, CLIENT_CONTRACT_VERSION, isContractNewer } from './build'

/**
 * Version detection (SPEC §12). Two independent facts:
 *  - frontend build: GET /version.json (static, no-cache) -> {build, builtAt}
 *  - API contract:   GET /api/v1/version (no-store)       -> {api_version, contract_version}
 * An update is warranted when the published build differs from the running one, or when the API
 * contract has a newer major.minor than the one this client was generated for.
 */

export interface CheckResult {
  /** Build id currently published, or null if it could not be read. */
  build: string | null
  contractVersion: string | null
  apiVersion: string | null
  buildChanged: boolean
  contractNewer: boolean
}

type Fetch = typeof fetch

async function readJson(fetchImpl: Fetch, url: string): Promise<Record<string, unknown> | null> {
  try {
    const response = await fetchImpl(url, { cache: 'no-store', credentials: 'omit', headers: { Accept: 'application/json' } })
    if (!response.ok) return null
    const body: unknown = await response.json() // SPA fallback HTML throws here -> null
    return body && typeof body === 'object' ? (body as Record<string, unknown>) : null
  } catch {
    return null
  }
}

const str = (v: unknown) => (typeof v === 'string' && v.length > 0 ? v : null)

export async function checkVersion(
  fetchImpl: Fetch = (...args) => fetch(...args),
  now: () => number = Date.now,
  current: { build: string; contract: string } = { build: APP_BUILD, contract: CLIENT_CONTRACT_VERSION },
): Promise<CheckResult | null> {
  const [web, api] = await Promise.all([
    readJson(fetchImpl, `/version.json?ts=${now()}`),
    readJson(fetchImpl, '/api/v1/version'),
  ])
  const build = str(web?.['build'])
  const contractVersion = str(api?.['contract_version'])
  if (!build && !contractVersion) return null
  return {
    build,
    contractVersion,
    apiVersion: str(api?.['api_version']),
    buildChanged: build !== null && build !== current.build,
    contractNewer: contractVersion !== null && isContractNewer(contractVersion, current.contract),
  }
}

export interface VersionCheckerOptions {
  check: () => Promise<CheckResult | null>
  onResult: (result: CheckResult) => void
  isOnline?: () => boolean
  /** Periodic interval while the app is open. */
  intervalMs?: number
  /** Minimum spacing for visibility/periodic triggers (start and `online` always run). */
  minSpacingMs?: number
  now?: () => number
  doc?: Pick<Document, 'addEventListener' | 'removeEventListener' | 'visibilityState'>
  win?: Pick<Window, 'addEventListener' | 'removeEventListener'>
}

export type CheckTrigger = 'start' | 'visible' | 'online' | 'interval' | 'manual'

export interface VersionChecker {
  start(): () => void
  checkNow(trigger?: CheckTrigger): Promise<CheckResult | null>
}

export const VERSION_CHECK_INTERVAL_MS = 15 * 60 * 1000

export function createVersionChecker(options: VersionCheckerOptions): VersionChecker {
  const {
    check,
    onResult,
    isOnline = () => navigator.onLine,
    intervalMs = VERSION_CHECK_INTERVAL_MS,
    minSpacingMs = 2000,
    now = Date.now,
    doc = document,
    win = window,
  } = options
  let inFlight: Promise<CheckResult | null> | null = null
  let lastRun = Number.NEGATIVE_INFINITY

  async function run(trigger: CheckTrigger): Promise<CheckResult | null> {
    if (inFlight) return inFlight
    const throttled = trigger === 'visible' || trigger === 'interval'
    if (throttled && now() - lastRun < minSpacingMs) return null
    if (!isOnline()) return null
    lastRun = now()
    inFlight = check()
      .then((result) => {
        if (result) onResult(result)
        return result
      })
      .catch(() => null)
      .finally(() => {
        inFlight = null
      })
    return inFlight
  }

  return {
    checkNow: (trigger = 'manual') => run(trigger),
    start() {
      const onVisibility = () => {
        if (doc.visibilityState === 'visible') void run('visible')
      }
      const onOnline = () => void run('online')
      doc.addEventListener('visibilitychange', onVisibility)
      win.addEventListener('online', onOnline)
      const timer = setInterval(() => void run('interval'), intervalMs)
      void run('start')
      return () => {
        doc.removeEventListener('visibilitychange', onVisibility)
        win.removeEventListener('online', onOnline)
        clearInterval(timer)
      }
    },
  }
}
